import type { useBookingActions } from '../hooks/use-booking-actions';
import { useGarageOptions } from '../hooks/use-garage-options';
import type { useScheduleDialogs } from '../hooks/use-schedule-dialogs';
import { BookingFormDrawer, type BookingFormValues } from './BookingFormDrawer';
import { CancelBookingModal } from './CancelBookingModal';

interface BookingDialogsProps {
  /** The truck a new booking is for; a list that is not one truck's passes none and cannot start a booking. */
  truckId?: string;
  dialogs: ReturnType<typeof useScheduleDialogs>;
  actions: ReturnType<typeof useBookingActions>;
}

/** The booking drawer (create or move) and the cancel modal, wired to the booking actions: one place for the tab and the list to share. */
export function BookingDialogs({ truckId, dialogs, actions }: BookingDialogsProps) {
  const garages = useGarageOptions();
  const { drawer } = dialogs;

  async function submit(values: BookingFormValues): Promise<boolean> {
    const window = { startsAt: values.startsAt.toISOString(), endsAt: values.endsAt.toISOString(), confirmConflicts: values.confirmConflicts };
    if (drawer?.kind === 'move') return (await actions.move(drawer.booking.id, window)).status === 'saved';
    if (!truckId) return false;
    const workshop = values.garageId === null ? { isInHouse: true } : { isInHouse: false, garageId: values.garageId };
    return (await actions.create({ truckId, ...workshop, ...window })).status === 'saved';
  }

  return (
    <>
      <BookingFormDrawer
        mode={drawer}
        garages={garages}
        error={actions.error}
        conflicts={actions.conflicts}
        submitting={actions.saving}
        onSubmit={submit}
        onEdited={actions.resetFeedback}
        onClose={() => {
          actions.resetFeedback();
          dialogs.closeDrawer();
        }}
      />
      <CancelBookingModal
        booking={dialogs.cancelling}
        error={actions.error}
        submitting={actions.saving}
        onSubmit={async (id, reason) => (await actions.cancel(id, reason)).status === 'saved'}
        onClose={() => {
          actions.resetFeedback();
          dialogs.closeCancel();
        }}
      />
    </>
  );
}
